import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Styles provided by RestaurantOnboarding.css

/* ── Fallback center: Quito, Ecuador ── */
const ECUADOR_CENTER = [-0.2295, -78.5243];

/* ── Bounding box half-side in degrees (~16 km) ── */
const SEARCH_DELTA = 0.15;

/* ── Haversine distance in meters between two lat/lng points ── */
const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const R  = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a  = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/* ── Brand-colored pin icon ── */
const PIN_ICON = new L.DivIcon({
  className: '',
  html: `
    <div style="position:relative;width:30px;height:42px;">
      <div style="
        width:30px;height:30px;
        background:linear-gradient(135deg,#FF8700,#C96400);
        border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);
        border:3px solid #fff;
        box-shadow:0 4px 14px rgba(0,0,0,.28);
        position:absolute;top:0;left:0;
      "></div>
      <div style="
        width:8px;height:8px;
        background:#fff;
        border-radius:50%;
        position:absolute;
        top:11px;left:11px;
      "></div>
    </div>
  `,
  iconSize:   [30, 42],
  iconAnchor: [15, 42],
});

/* ── Captures map clicks — uses ref so handler is never stale ── */
const MapClickHandler = ({ onMapClickRef }) => {
  useMapEvents({ click: (e) => onMapClickRef.current(e.latlng) });
  return null;
};

/* ── Coverage circle drawn with native Leaflet (bypasses react-leaflet v5 API) ── */
const CoverageCircle = ({ center, radius }) => {
  const map = useMap();
  useEffect(() => {
    if (!radius) return;
    const circle = L.circle(center, {
      radius,
      color:       '#F97316',
      weight:      2,
      dashArray:   '8 5',
      opacity:     0.65,
      fillColor:   '#F97316',
      fillOpacity: 0.08,
    }).addTo(map);
    return () => circle.remove();
  }, [map, center[0], center[1], radius]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

/* ── Flies the map to a target position (only when target changes) ── */
const MapFlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 17, { duration: 1.2 });
  }, [map, target]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

/* ── Main component ── */
const StepRestaurantLocation = ({ formData, onUpdate, errors = {}, onClearError }) => {
  const initPos =
    formData.latitude && formData.longitude
      ? [parseFloat(formData.latitude), parseFloat(formData.longitude)]
      : null;

  const cityLat = formData.city_lat ? parseFloat(formData.city_lat) : ECUADOR_CENTER[0];
  const cityLng = formData.city_lng ? parseFloat(formData.city_lng) : ECUADOR_CENTER[1];
  const cityCenter = [cityLat, cityLng];

  // Keep latest values in refs so map event handler is never stale
  const meetingAreaRef = useRef(formData.city_meeting_area ?? null);
  const cityLatRef     = useRef(cityLat);
  const cityLngRef     = useRef(cityLng);
  const onUpdateRef    = useRef(onUpdate);
  const onClearRef     = useRef(onClearError);
  meetingAreaRef.current = formData.city_meeting_area ?? null;
  cityLatRef.current     = cityLat;
  cityLngRef.current     = cityLng;
  onUpdateRef.current    = onUpdate;
  onClearRef.current     = onClearError;

  const [position,    setPosition]    = useState(initPos);
  const [flyTarget,   setFlyTarget]   = useState(null);
  const [outOfRange,  setOutOfRange]  = useState(formData.out_of_range || false);
  const [calleP,      setCalleP]      = useState(formData.address_main  || '');
  const [calleS,      setCalleS]      = useState(formData.address_cross || '');
  const [ref,         setRef]         = useState(formData.address_ref   || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching,   setSearching]   = useState(false);
  const [showDrop,    setShowDrop]    = useState(false);

  const searchTimer  = useRef(null);
  const searchRef    = useRef(null);

  /* Stable click handler ref — always reads latest values */
  const onMapClickRef = useRef(null);
  onMapClickRef.current = ({ lat, lng }) => {
    checkAndPlace(lat, lng, false);
  };

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target))
        setShowDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Check range + place pin — reads from refs, never stale */
  const checkAndPlace = (lat, lng, fly = false) => {
    const ma      = meetingAreaRef.current;
    const cLat    = cityLatRef.current;
    const cLng    = cityLngRef.current;
    const pos     = [lat, lng];

    setPosition(pos);
    if (fly) setFlyTarget(pos);

    const oor = ma != null
      ? haversineMeters(cLat, cLng, lat, lng) > ma
      : false;

    setOutOfRange(oor);
    onUpdateRef.current({ latitude: String(lat), longitude: String(lng), out_of_range: oor });
    if (!oor && onClearRef.current) {
      onClearRef.current('address');
      onClearRef.current('latitude');
    }
  };

  /* Merge streets + reference into formData.address */
  const mergeAddress = (main, cross, reference) => {
    const streets = main && cross ? `${main} y ${cross}` : main || cross || '';
    const merged  = streets && reference
      ? `${streets}, ${reference}`
      : streets || reference || '';
    onUpdate({ address: merged, address_main: main, address_cross: cross, address_ref: reference });
  };

  /* Nominatim search restricted to city bounding box */
  const fetchSuggestions = useCallback((q) => {
    clearTimeout(searchTimer.current);
    if (!q.trim()) { setSuggestions([]); setShowDrop(false); return; }
    setSearching(true);
    const viewbox = `${cityLng - SEARCH_DELTA},${cityLat + SEARCH_DELTA},${cityLng + SEARCH_DELTA},${cityLat - SEARCH_DELTA}`;
    searchTimer.current = setTimeout(async () => {
      try {
        const res  = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=6&accept-language=es&viewbox=${viewbox}&bounded=1`
        );
        const data = await res.json();
        setSuggestions(data);
        setShowDrop(data.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 420);
  }, [cityLat, cityLng]);

  const handleSuggestionSelect = (s) => {
    checkAndPlace(parseFloat(s.lat), parseFloat(s.lon), true);
    setSearchQuery(s.display_name.split(',')[0]);
    setShowDrop(false);
    setSuggestions([]);
  };

  /* Geolocation: place pin AND fly map there */
  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => checkAndPlace(coords.latitude, coords.longitude, true),
      () => {}
    );
  };

    return (
        <div className="ro-body ro-basic">
            <div className="ro-step-hd">
                <span className="ro-eyebrow ro-eyebrow--accent">Paso 6 · Dirección</span>
                <h2 className="ro-title">Ubicación del negocio</h2>
                <p className="ro-subtitle">
                    Primero completa tu dirección y al final confirma el punto exacto en el mapa.
                </p>
            </div>

            {/* ── Street fields (PRIMERO) ── */}
            <div className="ro-field">
                <label className="ro-lbl">
                    Calle principal<span className="ro-req">*</span>
                </label>
                <div className={`ro-inputwrap${errors.address_main ? ' ro-inputwrap--err' : ''}`}>
                    <span className="ro-inputico" aria-hidden>🏠</span>
                    <input
                        type="text"
                        className="ro-input--clean"
                        placeholder="Ej. Av. 6 de Diciembre"
                        value={calleP}
                        onChange={(e) => {
                            setCalleP(e.target.value);
                            mergeAddress(e.target.value, calleS, ref);
                            if (onClearError) onClearError('address_main');
                        }}
                    />
                </div>
                {errors.address_main && <span className="ro-err-msg">{errors.address_main}</span>}
            </div>

            <div className="ro-field">
                <label className="ro-lbl">
                    Calle secundaria<span className="ro-req">*</span>
                </label>
                <div className={`ro-inputwrap${errors.address_cross ? ' ro-inputwrap--err' : ''}`}>
                    <span className="ro-inputico" aria-hidden>↗️</span>
                    <input
                        type="text"
                        className="ro-input--clean"
                        placeholder="Ej. Av. Colón"
                        value={calleS}
                        onChange={(e) => {
                            setCalleS(e.target.value);
                            mergeAddress(calleP, e.target.value, ref);
                            if (onClearError) onClearError('address_cross');
                        }}
                    />
                </div>
                {errors.address_cross && <span className="ro-err-msg">{errors.address_cross}</span>}
            </div>

            <div className="ro-field">
                <label className="ro-lbl">
                    Referencia<span className="ro-req">*</span>
                </label>
                <div className={`ro-inputwrap${errors.address_ref ? ' ro-inputwrap--err' : ''}`}>
                    <span className="ro-inputico" aria-hidden>📌</span>
                    <input
                        type="text"
                        className="ro-input--clean"
                        placeholder="Ej. Frente al parque central"
                        value={ref}
                        onChange={(e) => {
                            setRef(e.target.value);
                            mergeAddress(calleP, calleS, e.target.value);
                            if (onClearError) onClearError('address_ref');
                        }}
                    />
                </div>
                {errors.address_ref && <span className="ro-err-msg">{errors.address_ref}</span>}
            </div>


            {/* ── Place search (DESPUÉS) ── */}
            <div className="ro-loc-search" ref={searchRef}>
                <div className={`ro-inputwrap${searching ? ' ro-inputwrap--busy' : ''}`}>
                    <span className="ro-inputico" aria-hidden>🔍</span>
                    <input
                        type="text"
                        className="ro-input--clean"
                        placeholder="Buscar lugar en la ciudad…"
                        value={searchQuery}
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            fetchSuggestions(e.target.value);
                        }}
                        onFocus={() => suggestions.length > 0 && setShowDrop(true)}
                    />
                    {searching && <span className="ro-spin ro-spin--sm" />}
                </div>

                {showDrop && (
                    <ul className="ro-loc-suggestions">
                        {suggestions.map((s, i) => (
                            <li
                                key={i}
                                className="ro-loc-suggestion"
                                onMouseDown={() => handleSuggestionSelect(s)}
                            >
                                <span className="ro-loc-sug-ico" aria-hidden>📍</span>
                                <span className="ro-loc-sug-name">
                {s.display_name.split(',').slice(0, 3).join(', ')}
              </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* ── Map (AL FINAL) ── */}
            <div className="ro-map-wrap">
                <MapContainer
                    center={position || cityCenter}
                    zoom={position ? 17 : 13}
                    className="ro-map"
                    scrollWheelZoom
                    zoomControl
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>'
                    />
                    <MapClickHandler onMapClickRef={onMapClickRef} />
                    <MapFlyTo target={flyTarget} />
                    <CoverageCircle
                        center={cityCenter}
                        radius={formData.city_meeting_area ?? 0}
                    />
                    {position && (
                        <Marker
                            position={position}
                            icon={PIN_ICON}
                            draggable
                            eventHandlers={{
                                dragend: (e) =>
                                    checkAndPlace(
                                        e.target.getLatLng().lat,
                                        e.target.getLatLng().lng,
                                        false
                                    ),
                            }}
                        />
                    )}
                </MapContainer>

                {formData.city_meeting_area && (
                    <div className="ro-map-legend">
                        <span className="ro-map-legend-dot" aria-hidden />
                        Zona de cobertura disponible para registrar tu tienda
                    </div>
                )}
                {!position && (
                    <div className={`ro-map-hint${errors.address ? ' ro-map-hint--err' : ''}`}>
                        Toca el mapa para colocar el pin 📍
                    </div>
                )}
                {errors.address && !outOfRange && (
                    <span className="ro-err-msg ro-err-msg--block">{errors.address}</span>
                )}
            </div>

            {outOfRange && (
                <div className="ro-out-of-range">
                    <span className="ro-out-of-range-ico" aria-hidden>⚠️</span>
                    <div>
                        <strong>Sin cobertura en esta ubicación</strong>
                        <p>Por el momento no tenemos servicio en ese punto. Mueve el pin dentro de la ciudad.</p>
                    </div>
                </div>
            )}

            <button type="button" className="ro-geo-btn" onClick={useMyLocation}>
                <span aria-hidden>📍</span> Usar mi ubicación actual
            </button>
        </div>
    );
};

export default StepRestaurantLocation;
